import React, { useState } from 'react';

import ClaimStepClosableMessage from './ClaimStepClosableMessage';
import Locale from '../../../Translate/Locale';
import SidePanelContentHeader from '../../../common2/side-panel/SidePanelContentHeader';
import { useClaimCarrierSelectOptions } from './useClaimCarrierSelectOptions';
import { isClaimWithMissingTrackingData } from '../claimsHelpers';
import EasySubmissionShippingInformation from './EasySubmissionShippingInformation';
import { ClaimContext, ClaimShopCarrier } from '../types';
import { ClaimStepContainerProps } from './ClaimStepContainer';
import * as yup from 'yup';
import { useYupValidation } from '../../../../hooks/useYupValidation';

export type EasyShippingInfoStepProps = {
  claim: ClaimContext;
  onChange: (data: Partial<ClaimContext>) => void;
  as: React.ComponentType<ClaimStepContainerProps>;
  showDeliveryInfo?: boolean;
  isManual: boolean;
  carriers: ClaimShopCarrier[];
} & ClaimStepContainerProps;

const schema = yup.object().shape({
  trackingCode: yup.string().required().label('Tracking ID'),
  recipientName: yup.string().required().label('Customer name'),
  recipientAddress: yup.string().required().label('Recipient address'),
  recipientCity: yup.string().required().label('Recipient city'),
  recipientZipCode: yup.string().required().label('Recipient zip code'),
  recipientCountryCode: yup.string().required().label('Recipient country'),
  isMultiShipmentOrder: yup
    .boolean()
    .label('Order type')
    .when('$isManual', {
      is: true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
});

function EasyShippingInfoStep(props: EasyShippingInfoStepProps) {
  const { as: Container, showDeliveryInfo, claim, onChange, onNext, isManual, carriers, ...restProps } = props;

  const { carrierOptions, carrierCountryOptions } = useClaimCarrierSelectOptions({
    carrierCode: claim.carrierCode,
    carrierCountryCode: claim.carrierCountryCode,
    carriers,
    type: claim.type,
  });

  const isTrackingDataMissing = isClaimWithMissingTrackingData(claim);

  const [isDisabled, setIsDisabled] = useState(false);

  const { validate, errors } = useYupValidation(schema);

  const onClickNext = () => {
    if (validate({ ...claim }, { isManual }) && onNext) {
      onNext();
    }
  };

  return (
    <Container {...restProps} onNext={onClickNext} isDisabled={isDisabled}>
      {(isManual || isTrackingDataMissing) && !claim.outsideOfDeadlineReason && (
        <ClaimStepClosableMessage intent="success">
          <Locale>CLAIMS_WITHIN_DEADLINE_MESSAGE</Locale>
        </ClaimStepClosableMessage>
      )}
      <SidePanelContentHeader>Shipping information</SidePanelContentHeader>
      <EasySubmissionShippingInformation
        claim={claim}
        carrierOptions={carrierOptions}
        carrierCountryOptions={carrierCountryOptions}
        onChange={onChange}
        isManual={isManual}
        errors={errors}
      />
    </Container>
  );
}

export default React.memo(EasyShippingInfoStep);
